import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { submitVolunteer } from "@/lib/api";
import { Marked, Asterisk, ArrowDraw } from "@/components/campaign/HandDrawn";

const VOLUNTEER_IMG = "https://images.pexels.com/photos/6590932/pexels-photo-6590932.jpeg";

const INTERESTS = [
  "Knocking doors",
  "Phone banking",
  "Hosting an event",
  "Yard sign",
  "Data & tech",
  "Writing / comms",
  "Translation",
  "Childcare for events",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  town: "",
  interests: [],
  availability: "",
  message: "",
};

const Volunteer = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const toggleInterest = (interest) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter((i) => i !== interest)
        : [...f.interests, interest],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share at least your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitVolunteer(form);
      toast.success("Thank you — we'll be in touch soon.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="volunteer-page">
      <section className="section border-b border-ink/25">
        <div className="container-x grid gap-12 md:grid-cols-12 items-end">
          <div className="md:col-span-8">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> Volunteer
            </p>
            <h1 className="mt-4 font-editorial font-semibold text-5xl md:text-7xl tracking-tight leading-[0.92] text-ink">
              <Marked>Doors</Marked> don&apos;t<br />
              knock themselves.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink/75 leading-relaxed text-lg">
              Tell us how you&apos;d like to help. We&apos;ll match you with a team and{" "}
              <span className="italic font-semibold text-terracotta text-base">reach out fast</span>.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-parchment section">
        <div className="container-x grid gap-14 md:grid-cols-12">
          <aside className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-terracotta -rotate-2" aria-hidden="true" />
              <img src={VOLUNTEER_IMG} alt="Volunteers organizing in community" className="relative w-full h-[420px] object-cover border-2 border-ink/85" />
            </div>
            <div className="mt-8 bg-white/5 border border-rule p-7">
              <p className="overline inline-flex items-center gap-2">
                <Asterisk className="h-4 w-4" /> Why volunteer
              </p>
              <p className="mt-4 text-ink/85 leading-relaxed">
                Grassroots wins are built one conversation at a time. An hour from you{" "}
                <span className="italic font-semibold text-terracotta text-sm">replaces a whole stack</span>{" "}
                of corporate mailers. That&apos;s the deal — and that&apos;s the win.
              </p>
            </div>
          </aside>

          <div className="md:col-span-7">
            <form onSubmit={onSubmit} className="bg-white/5 border border-rule p-8 space-y-6" data-testid="volunteer-form">
              <div className="grid gap-6 md:grid-cols-2">
                <Field label="Full name *" name="name" value={form.name} onChange={handle} testid="volunteer-name" />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={handle} testid="volunteer-email" />
                <Field label="Phone" name="phone" value={form.phone} onChange={handle} testid="volunteer-phone" />
                <Field label="Town / neighborhood" name="town" value={form.town} onChange={handle} testid="volunteer-town" />
              </div>

              <div>
                <label className="overline">How would you like to help?</label>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="volunteer-interests">
                  {INTERESTS.map((i) => {
                    const active = form.interests.includes(i);
                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => toggleInterest(i)}
                        className={`px-4 py-2 border-2 text-sm font-bold uppercase tracking-wide transition-all ${
                          active
                            ? "bg-terracotta text-cream border-ink"
                            : "border-ink text-ink hover:bg-ink hover:text-cream"
                        }`}
                        data-testid={`volunteer-interest-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {active && <Check className="inline mr-1 -mt-0.5" size={12} />}
                        {i}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field
                label="Availability"
                name="availability"
                value={form.availability}
                onChange={handle}
                placeholder="e.g. Saturdays, weekday evenings"
                testid="volunteer-availability"
              />

              <div>
                <label className="overline" htmlFor="message">Anything else?</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handle}
                  data-testid="volunteer-message"
                  className="mt-3 w-full border border-rule bg-white/5 px-4 py-3 text-ink focus:outline-none focus:border-terracotta"
                />
              </div>

              <div className="flex items-center gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                  data-testid="volunteer-submit"
                >
                  {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                  {loading ? "Submitting…" : "Sign me up"}
                </button>
                <span className="italic font-semibold text-terracotta text-sm">Let&apos;s go</span>
                <ArrowDraw className="h-5 w-12" />
              </div>

              {done && (
                <p className="text-sm font-bold uppercase tracking-wider text-terracotta" data-testid="volunteer-success">
                  Thanks! We received your info — a team member will reach out shortly.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, name, value, onChange, type = "text", placeholder, testid }) => (
  <div>
    <label className="overline" htmlFor={name}>{label}</label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-3 w-full border border-rule bg-white/5 px-4 py-3 text-ink focus:outline-none focus:border-terracotta"
    />
  </div>
);

export default Volunteer;
